import React, { useEffect, useLayoutEffect, useRef } from 'react'
import { X } from 'lucide-react'
import './popup-motion.css'

export type PopupContentVerticalAlign = 'top' | 'center' | 'bottom'
export type PopupLayoutVariant = 'center_modal' | 'full_screen' | 'sidebar_right' | 'banner_bottom' | 'flyout_right' | 'bottom_sheet' | 'sticky_bar'
const readNumber = (value: unknown, fallback: number): number => {
    const number = Number(value)
    return Number.isFinite(number) ? number : fallback
}

export interface PopupCloseButtonSettings {
    position?: 'top_right'|'top_left'|'bottom_right'|'bottom_left'
    top?: number; right?: number; size?: number; iconSize?: number
    backgroundColor?: string; color?: string
    cornerRadius?: number; strokeWidth?: number; borderWidth?: number; borderColor?: string
}

export const readCloseButtonSettings = (rawSnapshot: Record<string, any>, previewMode: 'desktop'|'mobile' = 'desktop') => {
    const base = (rawSnapshot.close_button || {}) as Record<string, any>
    const closeButton = previewMode === 'mobile' ? {...base,...(base.mobile || {})} : base
    const clamp = (key: string, fallback: number, min: number, max: number) => Math.min(max,Math.max(min,readNumber(closeButton[key],fallback)))
    const size=clamp('size',32,24,96)
    const borderWidth=clamp('borderWidth',0,0,6)
    return {
        position: (['top_left','bottom_left','bottom_right'].includes(closeButton.position) ? closeButton.position : 'top_right') as NonNullable<PopupCloseButtonSettings['position']>,
        top: clamp('top',12,0,120), right:clamp('right',12,0,120), size,
        iconSize:clamp('iconSize',16,8,size-borderWidth*2),
        backgroundColor:String(closeButton.backgroundColor || 'rgba(0,0,0,0.06)'), color:String(closeButton.color || '#374151'),
        cornerRadius:clamp('cornerRadius',size/2,0,96), strokeWidth:clamp('strokeWidth',2.4,0.5,5),
        borderWidth, borderColor:String(closeButton.borderColor || '#d1d5db'),
    }
}

const POPUP_LAYOUT_VARIANTS = new Set<PopupLayoutVariant>(['center_modal', 'full_screen', 'sidebar_right', 'banner_bottom', 'flyout_right', 'bottom_sheet', 'sticky_bar'])

export const readPopupLayoutSettings = (rawSnapshot: Record<string, any>, previewMode: 'desktop' | 'mobile' = 'desktop'): { variant: PopupLayoutVariant; maxWidth: number; contentVerticalAlign: PopupContentVerticalAlign } => {
    const base = rawSnapshot.layout
    const layout = previewMode === 'mobile' && typeof base === 'object' && base !== null ? { ...base, ...(base.mobile || {}) } : base
    const display = (rawSnapshot.display || {}) as Record<string, unknown>
    const rawVariant = typeof layout === 'string'
        ? layout
        : String(layout?.variant || display.layout || 'center_modal')
    const variant = POPUP_LAYOUT_VARIANTS.has(rawVariant as PopupLayoutVariant)
        ? rawVariant as PopupLayoutVariant
        : 'center_modal'

    return {
        variant,
        contentVerticalAlign: layout?.contentVerticalAlign === 'center' || layout?.contentVerticalAlign === 'bottom' ? layout.contentVerticalAlign : 'top',
        maxWidth: readNumber(
            typeof layout === 'object' && layout !== null ? layout.maxWidth : undefined,
            480,
        ),
    }
}

/** Fixed customer dialogs manage focus and background interaction; embedded editor canvases do not. */
export function usePopupDialog(isOpen: boolean, modal: boolean, onDismiss: () => void, reopenTarget?: string) {
    const overlayRef = useRef<HTMLDivElement>(null)
    const surfaceRef = useRef<HTMLDivElement>(null)
    const dismissRef = useRef(onDismiss)
    dismissRef.current = onDismiss
    const wasOpen = useRef(false)
    useLayoutEffect(() => {
        const overlay = overlayRef.current, surface = surfaceRef.current
        if (!overlay || !surface) return
        delete overlay.dataset.closingToTab
        if (wasOpen.current && !isOpen && reopenTarget) {
            // The reopening tab is committed in the same render as dismissal, including responsive placement.
            const target = overlay.parentElement?.querySelector<HTMLElement>(reopenTarget)
            if (target) {
                const from = surface.getBoundingClientRect(), to = target.getBoundingClientRect()
                if (from.width && from.height && to.width && to.height) {
                    // ERP preview canvases are scaled; transform distances are local CSS pixels.
                    const scaleX = surface.offsetWidth ? from.width / surface.offsetWidth : 1
                    const scaleY = surface.offsetHeight ? from.height / surface.offsetHeight : 1
                    surface.style.setProperty('--popup-close-x', `${(to.left + to.width / 2 - from.left - from.width / 2) / scaleX}px`)
                    surface.style.setProperty('--popup-close-y', `${(to.top + to.height / 2 - from.top - from.height / 2) / scaleY}px`)
                    surface.style.setProperty('--popup-close-scale', String(Math.min(0.35, to.width / from.width, to.height / from.height)))
                    overlay.dataset.closingToTab = 'true'
                }
            }
        }
        wasOpen.current = isOpen
        // aria-hidden alone does not remove closed form controls from keyboard navigation.
        if (isOpen) overlay.removeAttribute('inert')
        else overlay.setAttribute('inert', '')
    }, [isOpen, reopenTarget])
    useEffect(() => {
        if (!isOpen || !modal) return
        const overlay = overlayRef.current, surface = surfaceRef.current
        if (!overlay || !surface) return
        const previousFocus = document.activeElement as HTMLElement | null
        const previousOverflow = document.body.style.overflow
        const previousRootOverflow = document.documentElement.style.overflow
        document.body.style.overflow = 'hidden'
        document.documentElement.style.overflow = 'hidden'
        const background: Array<[Element, string | null]> = []
        let branch: Element = overlay
        while (branch.parentElement) {
            for (const sibling of Array.from(branch.parentElement.children)) {
                if (sibling === branch || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) continue
                background.push([sibling, sibling.getAttribute('inert')])
                sibling.setAttribute('inert', '')
            }
            branch = branch.parentElement
            if (branch === document.body) break
        }
        // Focus the surface rather than opening the mobile keyboard automatically.
        surface.focus({ preventScroll: true })
        const focusable = () => Array.from(surface.querySelectorAll<HTMLElement>('a[href],button,input,select,textarea,[tabindex],audio[controls],video[controls],summary')).filter(element =>
            element.tabIndex >= 0 && !element.matches(':disabled') && !element.closest('[inert],[hidden],[aria-hidden="true"]') && getComputedStyle(element).display !== 'none' && getComputedStyle(element).visibility !== 'hidden' && element.getClientRects().length > 0)
        const keydown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault(); event.stopPropagation(); dismissRef.current(); return
            }
            if (event.key !== 'Tab') return
            const items = focusable(), first = items[0], last = items[items.length - 1]
            if (!first) { event.preventDefault(); surface.focus(); return }
            if (event.shiftKey && (document.activeElement === first || document.activeElement === surface || !surface.contains(document.activeElement))) {
                event.preventDefault(); last.focus()
            } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === surface || !surface.contains(document.activeElement))) {
                event.preventDefault(); first.focus()
            }
        }
        const focusin = (event: FocusEvent) => { if (!surface.contains(event.target as Node)) surface.focus({ preventScroll: true }) }
        document.addEventListener('keydown', keydown, true)
        document.addEventListener('focusin', focusin)
        return () => {
            document.removeEventListener('keydown', keydown, true)
            document.removeEventListener('focusin', focusin)
            document.body.style.overflow = previousOverflow
            document.documentElement.style.overflow = previousRootOverflow
            for (const [element, inert] of background) {
                if (inert === null) element.removeAttribute('inert')
                else element.setAttribute('inert', inert)
            }
            const target = reopenTarget ? overlay.parentElement?.querySelector<HTMLElement>(reopenTarget) : null
            const restore = previousFocus?.isConnected && previousFocus !== document.body && !previousFocus.closest('[inert]') ? previousFocus : target
            restore?.focus({ preventScroll: true })
        }
    }, [isOpen, modal, reopenTarget])
    return { overlayRef, surfaceRef }
}

export interface PopupSurfaceProps {
    variant: PopupLayoutVariant
    accessibleLabel?: string
    reopenTarget?: string
    maxWidth?: number
    backgroundColor?: string
    cornerRadius?: number
    fillContentHeight?: boolean
    contentVerticalAlign?: PopupContentVerticalAlign
    animateEntrance?: boolean
    closeButton?: PopupCloseButtonSettings
    closeButtonSelected?: boolean
    isOpen?: boolean
    position?: 'fixed' | 'absolute'
    onDismiss: () => void
    onClose?: () => void
    children: React.ReactNode
}

/** Customer layout, shared verbatim with the ERP preview. All dimensions use the containing viewport. */
export function PopupSurface({ variant, accessibleLabel = 'Special offer', reopenTarget, maxWidth = 480, backgroundColor = '#ffffff', cornerRadius = 24, fillContentHeight = false, contentVerticalAlign = 'top', animateEntrance = true, closeButton = {}, closeButtonSelected = false, isOpen = true, position = 'fixed', onDismiss, onClose, children }: PopupSurfaceProps) {
    const nonmodal=variant==='flyout_right'||variant==='sticky_bar'
    const { overlayRef, surfaceRef } = usePopupDialog(isOpen, position === 'fixed'&&!nonmodal, onDismiss, reopenTarget)
    useEffect(()=>{if(!isOpen||!nonmodal||position!=='fixed')return;const escape=(e:KeyboardEvent)=>{if(e.key==='Escape')onDismiss()};document.addEventListener('keydown',escape);return()=>document.removeEventListener('keydown',escape)},[isOpen,nonmodal,position,onDismiss])
    const close = readCloseButtonSettings({close_button:closeButton})
    const corner = close.position
    const width = Number.isFinite(maxWidth) && maxWidth > 0 ? maxWidth : 480
    const overlay: React.CSSProperties = { position, inset: 0, zIndex: 9999, display: 'flex', pointerEvents: isOpen&&!nonmodal ? 'auto' : 'none', boxSizing: 'border-box' }
    const surface: React.CSSProperties = { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 16, lineHeight: 1.5, position: 'relative', backgroundColor, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflowY: 'auto', maxHeight: '100%', boxSizing: 'border-box', minWidth: 0, pointerEvents:isOpen?'auto':'none' }
    if (variant==='flyout_right'||variant==='bottom_sheet'||variant==='sticky_bar') {
        Object.assign(overlay,{alignItems:'flex-end',justifyContent:variant==='flyout_right'?'flex-end':'center',padding:variant==='sticky_bar'?0:16})
        Object.assign(surface,{width:'100%',maxWidth:variant==='sticky_bar'?undefined:width,maxHeight:'calc(100% - 64px)',borderRadius:variant==='sticky_bar'?0:cornerRadius})
    } else if (variant === 'full_screen') {
        Object.assign(overlay, { alignItems: 'stretch', justifyContent: 'stretch' })
        Object.assign(surface, { width: '100%', height: '100%' })
    } else if (variant === 'sidebar_right') {
        Object.assign(overlay, { alignItems: 'stretch', justifyContent: 'flex-end' })
        Object.assign(surface, { width: `min(${Math.min(width, 480)}px, 100%)`, height: '100%' })
    } else if (variant === 'banner_bottom') {
        Object.assign(overlay, { alignItems: 'flex-end', justifyContent: 'center' })
        Object.assign(surface, { width: '100%', borderTop: '1px solid rgba(229,231,235,1)', boxShadow: '0 -8px 30px rgba(0,0,0,0.12)' })
    } else {
        Object.assign(overlay, { alignItems: 'center', justifyContent: 'center', padding: 16 })
        Object.assign(surface, { width: '100%', maxWidth: width, maxHeight: 'calc(100% - 80px)', borderRadius: Math.max(0, Math.min(200, readNumber(cornerRadius, 24))) })
    }
    if(fillContentHeight && variant === 'center_modal') surface.height = 'calc(100% - 80px)'
    if(fillContentHeight && variant === 'banner_bottom') surface.height = '100%'
    return <div ref={overlayRef} className="journey-popup-overlay" data-open={isOpen} data-animate={animateEntrance} style={overlay} aria-hidden={!isOpen}>
        {!nonmodal&&<div onClick={onDismiss} style={{ position: 'absolute', inset: 0, backgroundColor: variant === 'banner_bottom' ? 'transparent' : 'rgba(0,0,0,0.45)' }} />}
        <div ref={surfaceRef} role={position === 'fixed' ? 'dialog' : undefined} aria-modal={position === 'fixed'&&!nonmodal ? true : undefined} aria-label={accessibleLabel} tabIndex={-1} className="journey-popup-surface" data-layout={variant} style={surface}>
            <button type="button" onClick={onClose || onDismiss} aria-label="Close popup" style={{
                position:'absolute', ...(corner.startsWith('top') ? {top:close.top} : {bottom:close.top}),
                ...(corner.endsWith('left') ? {left:close.right} : {right:close.right}),
                zIndex:5,width:close.size,height:close.size,borderRadius:close.cornerRadius,
                border:`${close.borderWidth}px solid ${close.borderColor}`,boxSizing:'border-box',padding:0,
                backgroundColor:close.backgroundColor,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:close.color,
                outline:closeButtonSelected?'2px solid #6366f1':undefined,outlineOffset:2,
            }}>
                <X size={close.iconSize} strokeWidth={close.strokeWidth} aria-hidden="true" />
            </button>
            {variant === 'sidebar_right' ? <div className="journey-popup-sidebar-content" style={{display:'flex',flexDirection:'column',minHeight:'100%', ...(fillContentHeight ? {height:'100%'} : {})}}>
                <div data-content-align={contentVerticalAlign} style={{flexShrink:0,marginTop:contentVerticalAlign === 'top' ? 0 : 'auto',marginBottom:contentVerticalAlign === 'bottom' ? 0 : 'auto', ...(fillContentHeight ? {display:'flex',flexDirection:'column',flex:'1 0 auto',minHeight:'100%'} : {})}}>{children}</div>
            </div> : children}
        </div>
    </div>
}
